import { useFrontendCore } from "@kosmod/core";
import { s, Shape } from "@kosmod/pancake";
import { runUpdater, StateSetter, useEffect, useMemo, useState } from "@lukekaalim/act";

import { Observable, Subject } from 'rxjs';

export type LocalStorageService = {
  get(key: string): string | null,
  set(key: string, value: string): void,
  remove(key: string): void,

  onStorageUpdate: Observable<{ modifiedKeys: string[] }>
}

export const createLocalStorageService = (localStorage: Storage = window.localStorage): LocalStorageService => {
  const onStorageUpdate = new Subject<{ modifiedKeys: string[] }>();

  return {
    get(key) {
      return localStorage.getItem(key);
    },
    set(key, value) {
      localStorage.setItem(key, value);
      onStorageUpdate.next({ modifiedKeys: [key] })
    },
    remove(key) {
      localStorage.removeItem(key);
      onStorageUpdate.next({ modifiedKeys: [key] })
    },
    onStorageUpdate,
  }
};

/**
 * A "in-memory" storage service for fakes and mocks
 */
export const createMemoryStorageService = (): LocalStorageService => {
  const values = new Map<string, string>();
  const onStorageUpdate = new Subject<{ modifiedKeys: string[] }>();

  return {
    get(key) {
      return values.get(key) || null;
    },
    set(key, value) {
      values.set(key, value);
      onStorageUpdate.next({ modifiedKeys: [key] })
    },
    remove(key) {
      values.delete(key);
      onStorageUpdate.next({ modifiedKeys: [key] })
    },
    onStorageUpdate,
  }
}

/**
 * Reads the local storage once. SetValue assignments
 * write to the storage afterward.
 * 
 * Beware: Don't call this hook twice with the same key
 * for the same storage, this leads to inconsistent
 * behavior
 * 
 * @param key 
 * @param shape 
 * @param defaultValue 
 * @param storage 
 * @returns 
 */
export const useLocalStorage = <T extends Shape>(
  key: string,
  shape: T,
  defaultValue: Shape.Type.Of<T>,
  storage: LocalStorageService
) => {

  const [value, setLocalValue] = useState<Shape.Type.Of<T>>(() => {
    const stored = storage.get(key);

    if (stored)
      return JSON.parse(stored) as Shape.Type.Of<T>;
    return defaultValue;
  })

  const setValue = useMemo((): StateSetter<Shape.Type.Of<T>> => (updater) => {
    setLocalValue(prev => {
      const nextValue = runUpdater(prev, updater);
      if (nextValue === defaultValue) {
        storage.remove(key);
      } else {
        storage.set(key, JSON.stringify(nextValue));
      }
      return nextValue;
    })
  }, [storage])
  return [value, setValue] as const;
}